import { Link } from "react-router-dom";
import { MapPin, Phone, MessageCircle, HeartHandshake, Recycle, ArrowRight } from "lucide-react";
import { SHOP } from "@/lib/api";
import Seo from "@/components/Seo";

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About Us | Appleman Tech Repair — Hyde, Tameside" description="Independent repair business in Hyde, Tameside. Phone, tablet, laptop and console repairs by online booking, community device repair with Tameside Community Computers CIC." />
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="eyebrow text-accent mb-4">About us</p>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl leading-[0.95] mb-8" data-testid="about-heading">
              Independent repair, done properly
            </h1>
            <div className="space-y-4 text-neutral-600 leading-relaxed">
              <p>
                Appleman Tech Repair is run by <strong>Tom Ward</strong>, based at 24 Springwell Gardens in Hyde, Tameside. Tom has spent <strong>over ten years in the device repair industry</strong> — and he grew up right here in the community he now works in. That shows in how the business runs: straight answers, fair prices and real care for local people.
              </p>
              <p>
                We fix phones, tablets, laptops and games consoles — quickly, honestly and with a warranty on every single repair.
              </p>
              <p>
                Repairs are by online booking only — book your slot and we'll be expecting your device. People come to us for fast turnaround — many repairs are done the same day — and for straight answers. If a repair isn't worth doing, we'll say so and tell you your options, whether that's a refurbished replacement or secure recycling.
              </p>
              <p>
                Can't make it to us? We accept post-in repairs from anywhere in the UK. Book online, post your device, and we'll message you when it lands and when it's fixed — then it comes back with free next-day return delivery, guaranteed.
              </p>
            </div>
          </div>
          <div className="relative" data-testid="about-photo-card">
            <div className="border-2 border-black shadow-hard-lg overflow-hidden">
              <img src="/tom.webp" alt="Tom Ward, owner of Appleman Tech Repair" className="w-full h-[480px] object-cover object-[50%_35%]" data-testid="about-tom-photo" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-accent border-2 border-black px-5 py-3 shadow-hard font-display font-bold uppercase text-sm tracking-wider">
              Tom Ward — owner
            </div>
            <img src="/logo.webp" alt="" aria-hidden="true" className="absolute -top-5 -right-5 h-16 w-16 object-contain bg-white border-2 border-black shadow-hard-sm p-1" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pb-20" data-testid="about-facts">
          {[
            ["10+ years", "in the device repair industry"],
            ["Born & raised", "in the Tameside community we serve"],
            ["Every repair", "covered by a warranty"],
          ].map(([big, small]) => (
            <div key={big} className="border-2 border-black bg-white p-6 shadow-hard brutal-hover text-center">
              <p className="font-display font-black uppercase tracking-tight text-3xl">{big}</p>
              <p className="text-sm text-neutral-500 mt-2">{small}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-b-2 border-black bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="border-2 border-black shadow-hard-lg overflow-hidden">
            <img src="https://images.pexels.com/photos/19037726/pexels-photo-19037726.jpeg?auto=compress&w=1000" alt="Phones stacked for recycling" className="img-ink w-full h-80 object-cover" data-testid="about-community-image" />
          </div>
          <div>
            <p className="eyebrow text-accent mb-4">Community first</p>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl mb-6" data-testid="about-community-heading">Fighting digital poverty in Tameside</h2>
            <div className="space-y-4 text-neutral-600 leading-relaxed">
              <p className="flex gap-3">
                <HeartHandshake className="h-6 w-6 shrink-0 text-accent mt-1" />
                <span>We work with <strong>Tameside Community Computers CIC</strong>, repairing donated smartphones and tablets free of charge. The refurbished devices go to local people living in digital poverty — because a working phone is a lifeline, not a luxury.</span>
              </p>
              <p className="flex gap-3">
                <Recycle className="h-6 w-6 shrink-0 text-accent mt-1" />
                <span>We also provide secure disposal and recycling for IT equipment — desktops, laptops and mobile devices — wiping data properly and keeping e-waste out of landfill.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="border-2 border-black shadow-hard p-8" data-testid="about-visit-card">
            <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-6">Visit or message us</h2>
            <ul className="space-y-4 text-neutral-600 mb-8">
              <li className="flex gap-3"><MapPin className="h-5 w-5 shrink-0 text-accent" /> {SHOP.address}</li>
              <li className="flex gap-3"><Phone className="h-5 w-5 shrink-0 text-accent" /> <a href={SHOP.phoneHref} className="hover:text-black" data-testid="about-phone-link">{SHOP.phone}</a></li>
              <li className="flex gap-3"><MessageCircle className="h-5 w-5 shrink-0 text-accent" /> <a href={SHOP.whatsapp} target="_blank" rel="noopener noreferrer" className="hover:text-black" data-testid="about-whatsapp-link">WhatsApp us anytime</a></li>
            </ul>
            <Link to="/book" data-testid="about-book-btn"
              className="inline-flex items-center gap-2 border-2 border-black bg-black text-white px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover">
              Book a repair <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="border-2 border-black shadow-hard p-8" data-testid="about-hours-card">
            <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-6">Opening hours</h2>
            <ul className="space-y-3">
              {SHOP.hours.map((h) => (
                <li key={h.day} className="flex justify-between border-b-2 border-neutral-100 pb-3">
                  <span className="font-medium">{h.day}</span>
                  <span className={h.time === "Closed" ? "text-neutral-400" : "font-display font-bold"}>{h.time}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-neutral-500">Outside hours? Book online or message us on WhatsApp and we'll pick it up next working day.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
